import React, { useState, useEffect } from 'react';
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { getItem } from "../../../../utils/localStorage";
import { toastify } from "../../../../utils/toast";
import {
  editContract,
  getLevies,
  getCustomersURL,
  getUnitsForFacility,
  getContracts,
  getTaxRates
} from "../../../../utils/urls";

// CSS styles to be added to your stylesheets
const styles = `
.form-section {
  margin-bottom: 1.5rem;
}

.section-header {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

.field {
  margin-bottom: 1rem;
}

.toggle-icon {
  cursor: pointer;
  font-size: 1.5rem;
  transition: all 0.2s ease;
}

.toggle-icon:hover {
  transform: scale(1.1);
}

.required-field::after {
  content: "*";
  color: #f44336;
  margin-left: 4px;
}
`;

const EditContract = ({
  visible,
  onHide,
  contractId,
  onSuccess,
  initialContract
}) => {
  // State management
  const [contractName, setContractName] = useState("");
  const [levy, setLevy] = useState("");
  const [customer, setCustomer] = useState("");
  const [unit, setUnit] = useState("");
  const [amount, setAmount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("Active");
  const [loading, setLoading] = useState(false);
  const [paymentFrequency, setPaymentFrequency] = useState("");
  const [balanceBroughtForward, setBalanceBroughtForward] = useState("0");
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [enabledTaxes, setEnabledTaxes] = useState([]);
  const [fetchedTaxRates, setFetchedTaxRates] = useState([]);
  const [digitaxEnabled, setDigitaxEnabled] = useState(false);

  // New edit reason state
  const [editReason, setEditReason] = useState("");

  // Data lists
  const [levies, setLevies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [existingContracts, setExistingContracts] = useState([]);

  // Initialize form with contract data
  useEffect(() => {
    if (visible && initialContract) {
      console.log('Loading initial contract:', initialContract);

      setContractName(initialContract.contractName || "");
      setLevy(initialContract.levyId || "");
      setCustomer(initialContract.customerId || "");
      setUnit(initialContract.unitId || "");
      setAmount(initialContract.amount?.toString() || "");
      setStartDate(initialContract.startDate ? new Date(initialContract.startDate).toISOString().split('T')[0] : "");
      setEndDate(initialContract.endDate ? new Date(initialContract.endDate).toISOString().split('T')[0] : "");
      setStatus(initialContract.status || "Active");
      setPaymentFrequency(initialContract.paymentFrequency || "");
      setBalanceBroughtForward(initialContract.balanceBroughtForward?.toString() || "0");
      setEnabledTaxes(initialContract.enabledTaxes || []);
      setDigitaxEnabled(initialContract.digitax?.enabled === true);
      // setTaxEnabled(initialContract.taxEnabled !== undefined ? initialContract.taxEnabled : true);

      // Reset edit reason on form load
      setEditReason("");

      loadInitialData();
    }
  }, [visible, initialContract]);

  const loadInitialData = async () => {
    if (visible) {
      await Promise.all([
        fetchLevies(),
        fetchCustomersForFacility(),
        fetchUnitsForFacility(),
        fetchExistingContracts()
      ]);
    }
  };

  // Fetch available levies
  const fetchLevies = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getLevies}/${facilityId}`,
        "GET",
        {}
      );

      if (response.success) {
        let leviesData = [];

        if (Array.isArray(response.data)) {
          leviesData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          leviesData = response.data.data;
        } else if (typeof response.data === 'object' && response.data !== null) {
          leviesData = [response.data];
        }

        const processedLevies = leviesData.map((levy) => ({
          _id: levy._id,
          levyName: levy.levyName || 'Untitled Levy',
          amount: levy.amount || 0,
          levyType: levy.levyType?.name || "N/A",
          collectionFrequency: levy.collectionFrequency || "Monthly"
        }));

        setLevies(processedLevies);
      } else {
        throw new Error(response.error || "Failed to fetch levies");
      }
    } catch (err) {
      console.error("Error in fetchLevies:", err);
      toastify(err.message || "Error fetching levies", "error");
      setLevies([]);
    }
  };

  // Fetch available customers for the facility
  const fetchCustomersForFacility = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getCustomersURL}/${facilityId}`,
        "GET",
        {}
      );
      if (response.success) {
        setCustomers(response.data);
      } else {
        throw new Error("Failed to fetch customers.");
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  // Fetch available units for the facility
  const fetchUnitsForFacility = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getUnitsForFacility}/${facilityId}`,
        "GET",
        {}
      );
      if (response.success) {
        const filteredUnits = response.data.filter(unit =>
          unit.tenantId || unit.homeOwnerId
        );
        setUnits(filteredUnits);
      } else {
        throw new Error("Failed to fetch units.");
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  // Fetch existing contracts
  const fetchExistingContracts = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getContracts}/${facilityId}`, "GET");
      console.log("LEVYResponse:", response);
      if (response.success) {
        const contractsData = response.data?.contracts || response.data || [];
        // Filter out the current contract being edited
        const filteredContracts = contractsData.filter(contract => contract._id !== contractId);
        setExistingContracts(filteredContracts);
      }
    } catch (error) {
      console.error("Error fetching existing contracts:", error);
      setExistingContracts([]);
    }
  };

  // Get frequency text for display
  const getFrequencyText = () => {
    if (!levy || !levies.length) return "";
    const selectedLevy = levies.find(l => l._id === levy);
    return selectedLevy ? selectedLevy.collectionFrequency : "";
  };

  const fetchTaxRates = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getTaxRates}/${facilityId}`,
        "GET"
      );
      if (response.success && response.data) {
        const taxData = response.data.data || response.data;
        const taxRatesArray = Array.isArray(taxData) ? taxData : [taxData];
        setFetchedTaxRates(taxRatesArray);
      }
    } catch (error) {
      console.error("Error fetching tax rates:", error);
      toastify("Error fetching tax rates", "error");
    }
  };

  const handleEditSubmit = async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      const userId = await getItem("userId");

      // Form validation
      if (!contractName || contractName.trim() === "") {
        toastify("Contract name is required.", "error");
        setLoading(false);
        return;
      }
      if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
        toastify("Amount must be a valid number.", "error");
        setLoading(false);
        return;
      }
      if (!startDate) {
        toastify("Start date is required.", "error");
        setLoading(false);
        return;
      }

      // Check if edit reason is provided
      if (!editReason || editReason.trim() === "") {
        toastify("Please provide a reason for editing the contract.", "error");
        setLoading(false);
        return;
      }

      // Check if dates are valid
      if (new Date(startDate) > new Date(endDate)) {
        toastify("Start date cannot be later than end date.", "error");
        setLoading(false);
        return;
      }

      // Track changes
      const changes = {};

      if (initialContract.contractName !== contractName) {
        changes.contractName = {
          old: initialContract.contractName,
          new: contractName
        };
      }

      if (initialContract.levyId !== levy) {
        changes.levyId = {
          old: initialContract.levyId,
          new: levy
        };
      }

      if (initialContract.startDate !== startDate) {
        changes.startDate = {
          old: initialContract.startDate,
          new: startDate
        };
      }

      if (initialContract.endDate !== endDate) {
        changes.endDate = {
          old: initialContract.endDate,
          new: endDate
        };
      }

      if (initialContract.status !== status) {
        changes.status = {
          old: initialContract.status,
          new: status
        };
      }

      if (parseFloat(initialContract.balanceBroughtForward || 0) !== parseFloat(balanceBroughtForward || 0)) {
        changes.balanceBroughtForward = {
          old: parseFloat(initialContract.balanceBroughtForward || 0),
          new: parseFloat(balanceBroughtForward || 0)
        };
      }

      const updatedData = {
        contractName: contractName.trim(),
        levyId: levy,
        customerId: customer,
        unitId: unit,
        amount: parseFloat(amount),
        startDate,
        endDate,
        status,
        balanceBroughtForward: parseFloat(balanceBroughtForward || 0),
        enabledTaxes,
        digitax: {
          enabled: digitaxEnabled
        },
        // taxEnabled: taxEnabled !== undefined ? Boolean(taxEnabled) : true,
        // Add edit history information
        editHistory: {
          editedBy: userId,
          reason: editReason.trim(),
          changes: Object.keys(changes).length > 0 ? changes : { general: "General update" }
        }
      };

      const response = await makeRequest2(
        `${editContract}/${facilityId}/${contractId}`,
        "PUT",
        updatedData
      );

      if (response.success) {
        toastify("Contract updated successfully", "success");
        onHide();
        onSuccess();
      } else {
        toastify(response.error || "Failed to update contract", "error");
      }
    } catch (err) {
      console.error('Error updating contract:', err);
      toastify(err.message || "Error updating contract", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaxRates();
  }, []);

  // Render edit reason section
  const renderEditReasonSection = () => (
    <div className="mb-4">
      <div className="card p-3 bg-light border-0 shadow-sm">
        <h5 className="mb-3 section-header">Edit Reason <span className="text-danger">*</span></h5>
        <div className="form-group">
          <InputTextarea
            className={`form-control ${!editReason.trim() ? 'is-invalid' : ''}`}
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            rows={3}
            placeholder="Please provide a reason for editing this contract"
          />
          {!editReason.trim() && (
            <small className="text-danger">Reason for edit is required</small>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      header="Edit Service Agreement"
      style={{ width: "70vw" }}
      visible={visible}
      onHide={onHide}
      footer={
        <div className="text-end mt-4">
          <Button
            type="button"
            className="btn btn-outline-secondary me-2"
            onClick={onHide}
            disabled={loading}
          >
            Cancel
          </Button>
          &nbsp;
          <Button
            type="button"
            className="btn btn-primary"
            onClick={handleEditSubmit}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </div>
      }
    >
      <div className="p-fluid">
        {/* Edit Reason Section - Added at the top for visibility */}
        {renderEditReasonSection()}

        <div className="card p-3 mb-4 border-0 shadow-sm">
          <h5 className="mb-3 section-header">Service Agreement Details</h5>
          <div className="grid">
            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Service Agreement Name <span className="text-danger">*</span></label>
              <InputText
                className={`form-control ${!contractName ? 'is-invalid' : ''}`}
                value={contractName}
                onChange={(e) => setContractName(e.target.value)}
              />
            </div>

            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Levy <span className="text-danger">*</span></label>
              <select
                className={`form-control ${!levy ? 'is-invalid' : ''}`}
                value={levy}
                onChange={(e) => setLevy(e.target.value)}
              >
                <option value="">-- Select --</option>
                {levies.map((levyItem) => (
                  <option key={levyItem._id} value={levyItem._id}>
                    {levyItem.levyName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Unit</label>
              <select
                className="form-control bg-light"
                value={unit}
                disabled
              >
                <option value="">-- Select --</option>
                {units.map((unitItem) => (
                  <option key={unitItem._id} value={unitItem._id}>
                    {unitItem.name}
                  </option>
                ))}
              </select>
              <small className="text-muted">Unit cannot be changed</small>
            </div>

            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Customer</label>
              <select
                className="form-control bg-light"
                value={customer}
                disabled
              >
                <option value="">-- Select --</option>
                {customers.map((customerItem) => (
                  <option key={customerItem._id} value={customerItem._id}>
                    {`${customerItem.firstName} ${customerItem.lastName}`}
                  </option>
                ))}
              </select>
              <small className="text-muted">Customer is tied to unit</small>
            </div>

            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Amount</label>
              <InputText
                type="number"
                className="form-control bg-light"
                value={amount}
                disabled
              />
              <small className="text-muted">Amount is managed in the levy configuration</small>
            </div>

            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Balance Brought Forward</label>
              <InputText
                type="number"
                className="form-control"
                value={balanceBroughtForward}
                onChange={(e) => setBalanceBroughtForward(e.target.value)}
                min="0"
                step="0.01"
              />
              <small className="text-muted">Current balance carried over from previous period</small>
            </div>



            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Applicable Taxes</label>

              {fetchedTaxRates.length > 0 ? (
                fetchedTaxRates.map((tax) => (
                  <div className="form-check" key={tax._id}>
                    <Checkbox
                      inputId={`levy-tax-${tax._id}`}
                      checked={enabledTaxes.includes(tax._id)}
                      onChange={(e) => {
                        let updated;

                        if (e.checked) {
                          updated = [...enabledTaxes, tax._id];
                        } else {
                          updated = enabledTaxes.filter(id => id !== tax._id);
                        }

                        setEnabledTaxes(updated);
                      }}
                      className="form-check-input"
                    />
                    <label htmlFor={`levy-tax-${tax._id}`} className="form-check-label ms-2">
                      {tax.taxType.toUpperCase()} ({tax.taxRate}%)
                    </label>
                  </div>
                ))
              ) : (
                <small className="text-muted">
                  No taxes configured for this facility
                </small>
              )}

              <small className="text-muted d-block mt-2">
                Select one or more taxes to apply to levy
              </small>
            </div>


            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Digitax Configuration</label>
              <div className="form-check">
                <Checkbox
                  inputId="digitaxToggle"
                  checked={digitaxEnabled}
                  onChange={(e) => setDigitaxEnabled(e.checked)}
                  className="form-check-input"
                />
                <label htmlFor="digitaxToggle" className="form-check-label ms-2">
                  <span className={`badge ${digitaxEnabled ? 'bg-success' : 'bg-secondary'}`}>
                    {digitaxEnabled ? 'ON' : 'OFF'}
                  </span>{' '}
                  Enable Digitax
                </label>
              </div>
              <small className="text-muted d-block mt-2">
                Sync this contract's tax records with Digitax
              </small>
            </div>

            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Payment Frequency</label>
              <InputText
                className="form-control bg-light"
                value={getFrequencyText()}
                disabled
              />
              <small className="text-muted">This value is set in the levy configuration</small>
            </div>
          </div>
        </div>

        <div className="card p-3 mb-4 border-0 shadow-sm">
          <h5 className="mb-3 section-header">Contract Period & Status</h5>
          <div className="grid">
            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Start Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-control ${!startDate ? 'is-invalid' : ''}`}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">End Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-control ${!endDate ? 'is-invalid' : ''}`}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="col-12 md:col-6 mb-3">
              <label className="form-label mb-2">Status</label>
              <div className="d-flex align-items-center p-2 border rounded">
                <i
                  className={`ti ${status === "Inactive"
                    ? "ti-toggle-left text-danger"
                    : "ti-toggle-right text-primary"
                    } toggle-icon`}
                  style={{ fontSize: "34px", cursor: "pointer" }}
                  onClick={() =>
                    setStatus(
                      status === "Inactive" ? "Active" : "Inactive"
                    )
                  }
                />
                <span className={`ms-2 font-medium ${status === "Active" ? "text-success" : "text-danger"}`}>
                  {status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Information Note */}
        <div className="alert alert-info">
          <i className="ti ti-info-circle me-2"></i>
          <strong>Note:</strong> GL Account configuration is managed at the levy level.
          Payment methods and other financial settings are also configured in the levy setup.
        </div>
      </div>
    </Dialog>
  );
};

export default EditContract;